import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronDown } from "react-icons/io5";
import { InlineSpinner } from "./SourceMessageAccordion";
import { timestampFormatter, TIMESTAMP_MINUTE_FORMAT } from "../../../core/utils/timestampFormatter";
import { buildActionItemSourceDeepLink } from "../actionItemHelpers";

/**
 * Short label for the last CRM write (Activity summary third column).
 *
 * @example
 * formatCrmLastWriteSummaryLabel("2026-07-24T10:30:00Z"); // "Jul 24"
 */
export const formatCrmLastWriteSummaryLabel = (timestamp) => {
  const formatted = timestampFormatter(timestamp, TIMESTAMP_MINUTE_FORMAT);
  if (!formatted || formatted === "Invalid Date") {
    return "—";
  }
  const datePart = formatted.split(",")[0];
  return datePart || formatted;
};

/**
 * Human-readable CRM object type label for activity rows.
 *
 * @example
 * formatCrmObjectTypeLabel("people"); // "Person"
 * formatCrmObjectTypeLabel("deals"); // "Deal"
 */
export const formatCrmObjectTypeLabel = (objectType) => {
  const normalized = String(objectType || "").trim().toLowerCase();
  const labels = {
    people: "Person",
    person: "Person",
    companies: "Company",
    company: "Company",
    deals: "Deal",
    deal: "Deal",
    contact: "Contact",
    account: "Account",
    opportunity: "Opportunity",
    lead: "Lead",
  };
  if (labels[normalized]) {
    return labels[normalized];
  }
  if (!normalized) {
    return "Record";
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * Build a display label for a CRM write activity row.
 *
 * @example
 * buildCrmWriteDisplayLabel({
 *   action_item_title: "Follow up with Jane",
 *   crm_object_type: "people",
 * });
 * // "Follow up with Jane"
 */
export const buildCrmWriteDisplayLabel = (entry) => {
  const title = String(entry?.action_item_title || "").trim();
  if (title) {
    return title;
  }
  const objectType = formatCrmObjectTypeLabel(entry?.crm_object_type);
  const recordId = String(entry?.crm_record_id || "").trim();
  if (recordId) {
    return `${objectType} ${recordId.slice(0, 8)}`;
  }
  return objectType;
};

/**
 * One CRM write row inside a write-history accordion body.
 *
 * @param {object} props
 * @param {object} props.entry - CRM write history entry.
 * @param {string} props.providerLabel - e.g. "Attio" or "Salesforce".
 * @param {(entry: object) => void} props.onViewTask
 */
const CrmWriteHistoryItem = ({ entry, providerLabel, onViewTask }) => {
  const [expanded, setExpanded] = useState(false);
  const displayLabel = buildCrmWriteDisplayLabel(entry);
  const objectTypeLabel = formatCrmObjectTypeLabel(entry?.crm_object_type);
  const recordUrl = String(entry?.record_url || "").trim();
  const canExpand = displayLabel.length > 42;
  const canViewTask = Boolean(
    String(entry?.file_id || "").trim() && String(entry?.action_item_title || "").trim()
  );

  return (
    <li className="ds-sync-history__fetched-item">
      <div className="ds-sync-history__fetched-item-row">
        <span
          className={`ds-sync-history__source-title${
            expanded ? " ds-sync-history__source-title--expanded" : ""
          }${canExpand ? " ds-sync-history__source-title--expandable" : ""}`}
          onClick={() => {
            if (canExpand) setExpanded((value) => !value);
          }}
          onKeyDown={(event) => {
            if (canExpand && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              setExpanded((value) => !value);
            }
          }}
          role={canExpand ? "button" : undefined}
          tabIndex={canExpand ? 0 : undefined}
          title={canExpand ? displayLabel : undefined}
        >
          {displayLabel}
        </span>
        <span className="ds-sync-history__fetched-time"> · {objectTypeLabel}</span>
        {recordUrl ? (
          <a
            href={recordUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-sync-history__filter-btn"
          >
            View in {providerLabel} ↗
          </a>
        ) : null}
        {canViewTask ? (
          <button
            type="button"
            className="ds-sync-history__filter-btn"
            onClick={() => onViewTask(entry)}
          >
            View task
          </button>
        ) : null}
      </div>
    </li>
  );
};

/**
 * One CRM write as an accordion row (date + summary header, details when expanded).
 *
 * @param {object} props
 * @param {object} props.entry - CRM write history entry.
 * @param {string} props.providerLabel - e.g. "Attio".
 */
const CrmWriteHistoryAccordionRow = ({ entry, providerLabel }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const updatedAtMs = Number(entry?.updated_at_ms) || 0;
  const objectTypeLabel = formatCrmObjectTypeLabel(entry?.crm_object_type);
  const dateLabel =
    updatedAtMs > 0
      ? timestampFormatter(updatedAtMs, { includeSeconds: false })
      : "Latest write";
  const headerSummary = `${objectTypeLabel} record written`;

  const handleViewTask = (writeEntry) => {
    const path = buildActionItemSourceDeepLink({
      workflow_id: writeEntry?.workflow_id,
      file_id: writeEntry?.file_id,
      action_item: writeEntry?.action_item_title,
    });
    if (path) {
      navigate(path);
    }
  };

  return (
    <div
      className={`ds-sync-history__row${open ? " ds-sync-history__row--open" : ""}`}
    >
      <button
        type="button"
        className="ds-sync-history__row-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ds-sync-history__row-main">
          <span className="ds-sync-history__date">{dateLabel}</span>
          <span className="ds-sync-history__summary">{headerSummary}</span>
        </span>
        <IoChevronDown
          className={`ds-sync-history__row-chevron${open ? " ds-sync-history__row-chevron--open" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="ds-sync-history__row-body">
          <div className="ds-sync-history__fetched">
            <span className="ds-sync-history__fetched-label">Objects written</span>
            <ul className="ds-sync-history__fetched-list">
              <CrmWriteHistoryItem
                entry={entry}
                providerLabel={providerLabel}
                onViewTask={handleViewTask}
              />
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Activity totals and per-write history for CRM connector settings.
 * Mirrors WorkflowSyncActivityPanel styling for digest connectors.
 *
 * @param {object} props
 * @param {string} props.providerLabel - Connector display name, e.g. "Attio".
 * @param {object|null} props.activity - Cumulative + history payload.
 * @param {boolean} [props.loading=false] - Show loading spinner.
 * @param {string} [props.className=""] - Extra root class.
 *
 * @example
 * <CrmWriteActivityPanel
 *   providerLabel="Attio"
 *   activity={{
 *     cumulative: { writes_count: 2, last_written_at: "2026-07-24T10:30:00Z" },
 *     history: [{ crm_object_type: "people", record_url: "https://...", updated_at_ms: 1 }],
 *   }}
 * />
 */
const CrmWriteActivityPanel = ({
  providerLabel = "CRM",
  activity,
  loading = false,
  className = "",
}) => {
  const history = Array.isArray(activity?.history) ? activity.history : [];
  const writesCount = Number(activity?.cumulative?.writes_count) || 0;
  const linkedCount = history.filter((entry) => String(entry?.record_url || "").trim()).length;
  const lastWriteLabel = formatCrmLastWriteSummaryLabel(activity?.cumulative?.last_written_at);
  const rootClassName = ["ds-detail__sync-stats", className].filter(Boolean).join(" ");

  return (
    <div className={rootClassName}>
      <h4 className="ds-sync-history__title">Activity</h4>
      {loading ? (
        <InlineSpinner text="Loading activity…" />
      ) : (
        <>
          <div className="ds-sync-summary">
            <div className="ds-sync-summary__item">
              <strong>{writesCount}</strong>
              <span>{writesCount === 1 ? "confirmed write" : "confirmed writes"}</span>
            </div>
            <div className="ds-sync-summary__item">
              <strong>{linkedCount || writesCount}</strong>
              <span>{linkedCount === 1 ? "object linked" : "objects linked"}</span>
            </div>
            <div className="ds-sync-summary__item">
              <strong>{lastWriteLabel}</strong>
              <span>last write</span>
            </div>
          </div>
          {history.length ? (
            <div className="ds-sync-history">
              <h4 className="ds-sync-history__title">Write history</h4>
              <div className="ds-sync-history__list">
                {history.map((entry, index) => (
                  <CrmWriteHistoryAccordionRow
                    key={entry?.item_key || `${entry?.updated_at_ms || index}`}
                    entry={entry}
                    providerLabel={providerLabel}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CrmWriteActivityPanel;
